import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, CheckCircle2, Lock, Truck, Clock, TrendingUp, ShieldCheck } from 'lucide-react';
import { subscribeToPush } from '../utils/pushNotification';
import { useAuth } from '../context/AuthContext';

const NotificationModal = () => {
  const location = useLocation();
  const skipOnPage =
    location.pathname === '/siparis-basarili' ||
    location.pathname === '/siparis-teyit';
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showBlockedHelp, setShowBlockedHelp] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (skipOnPage) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    // Bu oturumda "Sonra" denildiyse gösterme; uygulama yeniden açılınca sessionStorage sıfırlanır
    if (sessionStorage.getItem('notificationPromptDismissedSession')) return;

    // Eski kalıcı bayrak — artık kullanılmıyor, temizle
    localStorage.removeItem('notificationPromptShown');

    const mobile =
      window.innerWidth <= 768 ||
      window.matchMedia('(display-mode: standalone)').matches ||
      !!window.navigator.standalone;

    setIsMobile(mobile);

    let timeoutId = null;

    const checkAndShow = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent) {
        const delay = mobile ? 800 : 1500;
        timeoutId = setTimeout(() => setShowPrompt(true), delay);
        return true;
      }
      return false;
    };

    if (checkAndShow()) {
      return () => { if (timeoutId) clearTimeout(timeoutId); };
    }

    const interval = setInterval(() => {
      if (checkAndShow()) clearInterval(interval);
    }, 500);

    return () => {
      clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [skipOnPage]);

  if (skipOnPage) return null;

  // ── BUTON CLICK: Doğrudan senkron user-gesture ─────────────────────────
  const handleRequestPermission = () => {
    setShowBlockedHelp(false);

    if (!('Notification' in window)) {
      alert('Bu tarayıcı bildirimleri desteklemiyor.');
      return;
    }

    // Zaten izin verilmişse direkt abone ol
    if (Notification.permission === 'granted') {
      setIsSubscribing(true);
      const emailToUse = user?.email || localStorage.getItem('guestEmail') || null;
      subscribeToPush({ role: 'user', userId: user?.id, email: emailToUse })
        .then(() => setShowPrompt(false))
        .catch(() => setShowPrompt(false))
        .finally(() => setIsSubscribing(false));
      return;
    }

    if (Notification.permission === 'denied') {
      setShowBlockedHelp(true);
      return;
    }

    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        setIsSubscribing(true);
        const emailToUse = user?.email || localStorage.getItem('guestEmail') || null;
        subscribeToPush({ role: 'user', userId: user?.id, email: emailToUse })
          .then(() => {
            try {
              new Notification('🌟 Carmen Gold Bildirimleri Aktif!', {
                body: 'Sipariş durumunuz ve fiyat değişimleri kilit ekranınıza gelecek.',
                icon: '/favicon.ico',
              });
            } catch (_) {}
            setTimeout(() => setShowPrompt(false), 600);
          })
          .catch(() => setShowPrompt(false))
          .finally(() => setIsSubscribing(false));
      } else if (permission === 'denied') {
        setShowBlockedHelp(true);
      }
    });
  };

  const handleDismiss = () => {
    sessionStorage.setItem('notificationPromptDismissedSession', '1');
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  // ── MASAÜSTÜ TOAST ───────────────────────────────────────────────────────
  if (!isMobile) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 25, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 15, scale: 0.96 }}
          transition={{ type: 'spring', damping: 22, stiffness: 280 }}
          className="fixed bottom-6 right-6 z-[999999] w-88 bg-white rounded-2xl shadow-2xl border border-amber-100 overflow-hidden"
          style={{ pointerEvents: 'all' }}
        >
          {/* Gold Accent Top Strip */}
          <div className="h-1.5 bg-gradient-to-r from-[#D4AF37] via-[#F3E5AB] to-[#B8941F]" />

          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/15 rounded-xl flex items-center justify-center flex-shrink-0 shadow-inner">
                  <Bell className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h4 className="text-base font-['Cormorant_Garamond'] font-bold text-[#1a1a1a] leading-tight">
                    Anlık Bildirimleri Açın
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Sipariş ve altın fiyat güncellemeleri
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Advantages List */}
            <div className="bg-amber-50/50 rounded-xl p-3 mb-4 space-y-2 border border-amber-100/60">
              <div className="flex items-start gap-2.5 text-xs text-gray-700">
                <Truck className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Kargo ve teslimat durumunuz kilit ekranınıza gelsin</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs text-gray-700">
                <Clock className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Havale/EFT süre sonu uyarıları anında cebinizde</span>
              </div>
              <div className="flex items-start gap-2.5 text-xs text-gray-700">
                <TrendingUp className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Canlı altın fiyat değişimleri ve sürpriz fırsatlar</span>
              </div>
            </div>

            {showBlockedHelp && (
              <div className="mb-3 p-2.5 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-2">
                <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  Bildirim engellenmiş. Adres çubuğundaki kilit (🔒) simgesinden <strong>İzin Ver</strong> yapabilirsiniz.
                </p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRequestPermission}
                disabled={isSubscribing}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white rounded-xl hover:opacity-95
                           transition-all duration-300 font-medium text-xs shadow-md hover:shadow-lg
                           disabled:opacity-60 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {isSubscribing ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Kaydediliyor...</span>
                  </>
                ) : (
                  <>
                    <Bell className="w-3.5 h-3.5" />
                    <span>Bildirimleri Aç</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleDismiss}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200
                           transition-all duration-300 font-medium text-xs cursor-pointer"
              >
                Sonra
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // ── MOBİL BOTTOM SHEET ──────────────────────────────────────────────────
  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[999999] flex items-end" style={{ pointerEvents: 'all' }}>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleDismiss}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        />

        {/* Bottom Sheet */}
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 26, stiffness: 280 }}
          className="relative w-full bg-white rounded-t-3xl shadow-2xl p-6 z-10"
        >
          {/* Top handle */}
          <div className="w-12 h-1.5 bg-[#D4AF37]/40 rounded-full mx-auto mb-5" />

          {/* Close */}
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Icon + Title */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#D4AF37]/15 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-inner">
              <Bell className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-xl font-['Cormorant_Garamond'] font-bold text-[#1a1a1a] leading-snug">
                Bildirimleri Aktif Hale Getirin
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Siparişinizi cebinizden canlı takip edin
              </p>
            </div>
          </div>

          {/* Blocked help */}
          {showBlockedHelp && (
            <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-300 flex items-start gap-2">
              <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-relaxed">
                Bildirim izni engellenmiş. Adres çubuğundaki kilit (🔒) simgesinden <strong>İzin Ver</strong> seçeneğini aktifleştirin.
              </p>
            </div>
          )}

          {/* Advantages Bullets */}
          <div className="bg-amber-50/60 rounded-2xl p-4 mb-6 space-y-3 border border-amber-100">
            <div className="flex items-center gap-3 text-xs text-gray-700">
              <Truck className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
              <span><strong>Kargo ve Teslimat:</strong> Sipariş adımları telefon kilit ekranınıza düşer</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-700">
              <Clock className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
              <span><strong>Süre Hatırlatıcı:</strong> Havale/EFT siparişlerinin iptal riski uyarısı</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-700">
              <TrendingUp className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
              <span><strong>Canlı Altın Takibi:</strong> Fiyat değişimlerinde hızlı bildirimler</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              onClick={handleRequestPermission}
              disabled={isSubscribing}
              className="w-full px-6 py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white rounded-xl hover:opacity-95
                         transition-all duration-300 font-semibold text-sm shadow-lg hover:shadow-xl
                         disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSubscribing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Abonelik Kaydediliyor...</span>
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  <span>Bildirimleri Aç & Takip Et</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleDismiss}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200
                         transition-all duration-300 font-medium text-xs cursor-pointer"
            >
              Şimdi Değil
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default NotificationModal;
